import { useEffect, useState } from "react";
import { useFocusable, FocusContext } from "@noriginmedia/norigin-spatial-navigation";
import { disconnectAndReboot } from "@upshow/brightsign";
import { safeSetFocus } from "../epg/utils/shared/focus";
import useKeyboardNavigation from "./hooks/useKeyboardNavigation";
import useWifiSetupState from "./hooks/useWifiSetupState";
import SetupButton from "./components/SetupButton";
import "./WifiSetup.scss";

const daznLogo = `https://cdn.upshow.tv/ui-assets/DAZNForBusiness-Inline-White.png`;

// Internal component that only mounts when modal is open
const WifiSetupModal = ({ closeSetup }) => {
    const [isLoading, setIsLoading] = useState(false);

    const { ref, focusKey } = useFocusable({
        isFocusBoundary: true,
    });

    useEffect(() => {
        safeSetFocus("WIFI_CANCEL")
    }, []);

    const handleContinue = async () => {
        setIsLoading(true);
        await disconnectAndReboot();
    };

    const handleCancel = () => {
        closeSetup();
    };

    return (
        <FocusContext.Provider value={focusKey}>
            <div className="wifi-setup-backdrop">
                <div ref={ref} className="wifi-setup-container">
                    <div className="wifi-setup-content">
                        <h1 className="wifi-setup-title">Continue with Wi-Fi Configuration?</h1>
                        <div className="wifi-setup-description">
                            <p>Selecting "Continue" will restart your device and</p>
                            <p>forget your current network</p>
                        </div>
                        <div className="wifi-setup-buttons">
                            <SetupButton
                                text="Continue"
                                onPress={handleContinue}
                                focusKey="WIFI_CONTINUE"
                                isLoading={isLoading}
                            />
                            <SetupButton
                                text="Cancel"
                                onPress={handleCancel}
                                focusKey="WIFI_CANCEL"
                            />
                        </div>
                    </div>
                    <div className="wifi-setup-logo">
                        <img src={daznLogo} alt="DAZN For Business" />
                    </div>
                </div>
            </div>
        </FocusContext.Provider>
    );
};

const WifiSetup = () => {
    const { isOpen, openSetup, closeSetup } = useWifiSetupState();

    // Navigation hook (only opens if NO internet, closes with Escape/Backspace)
    useKeyboardNavigation(openSetup, closeSetup, isOpen);

    // Manage app root class as EPG
    useEffect(() => {
        const appRoot = document.getElementById('app-root');
        if (!appRoot) return;

        if (isOpen) {
            appRoot.classList.add('wifi-setup-active');
        } else {
            appRoot.classList.remove('wifi-setup-active');
        }

        return () => {
            appRoot.classList.remove('wifi-setup-active');
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return <WifiSetupModal closeSetup={closeSetup} />;
};

export default WifiSetup;
